import { atom } from "jotai";
import { atomWithStorage, createJSONStorage } from "jotai/utils";
import type { SetStateAction } from "react";
import type { SupportedLocale } from "@/i18n/locales";

/** Set while a deployment app is open: the dashboard chrome around it
 *  (sidebar, header) speaks the app's language. */
export const deploymentLocaleAtom = atom<SupportedLocale | undefined>(
  undefined
);

// The pin the user chose, shared by every tab and kept across sessions.
export const pinnedCollapsibleAtom = atomWithStorage("isCollapsible", false);

// Set while Genie has collapsed this tab's sidenav. It belongs to the tab
// (sessionStorage), like Genie itself, so the user's pin never changes for
// other tabs or later sessions. The server gets an empty stand-in.
export const collapsedForGenieAtom = atomWithStorage(
  "genie-collapsed-sidenav",
  false,
  createJSONStorage<boolean>(() =>
    typeof window === "undefined"
      ? { getItem: () => null, setItem: () => {}, removeItem: () => {} }
      : window.sessionStorage
  ),
  { getOnInit: true }
);

// Whether the sidenav collapses to a rail. Pinning it (false) also takes back
// the collapse Genie made.
export const isCollapsibleAtom = atom(
  (get) => get(pinnedCollapsibleAtom) || get(collapsedForGenieAtom),
  (get, set, value: SetStateAction<boolean>) => {
    const next =
      typeof value === "function" ? value(get(isCollapsibleAtom)) : value;
    set(pinnedCollapsibleAtom, next);
    if (!next) set(collapsedForGenieAtom, false);
  }
);
export const sidenavRevealAtom = atom(false);
// Set by unpinning: the pointer is still over the sidebar, but it should
// collapse at once and only open again after the pointer leaves and returns.
export const sidenavHoverSuppressedAtom = atom(false);
// Where a tool page draws its Build mode sidenav: the dashboard's sidenav
// slot, so tools come and go like every other sidenav.
export const toolSidenavTargetAtom = atom<HTMLElement | null>(null);
